import { ExternalFileBrowserDialog } from "@/components/ExternalConnections/ExternalFileBrowserDialog";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useGroupUuid } from "@/hooks/use-group-uuid";
import { useKitUuid } from "@/hooks/use-kit-uuid";
import { LIBRARY_FILE_ALLOWED_TEXT } from "@/lib/consts";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCloudUpload,
  faPaperclip,
  faUpload,
} from "@nnr/fontawesome-sharp-light/icons/sharp/light";
import { useState } from "react";
import { toast } from "sonner";
import { LibraryModal } from "../LibraryFileDialog";
import { MAX_FILES_COUNT, useChatFileManager } from "./ChatFileManagerContext";

type ChatFileManagerAttachmentButtonProps = {
  /**
   * Requires groupUuid and kitUuid to be provided in the ChatFileManager context.
   */
  library: boolean;
  disabled: boolean;
};

export const ChatFileManagerAttachmentButton: React.FC<
  ChatFileManagerAttachmentButtonProps
> = (props: ChatFileManagerAttachmentButtonProps) => {
  const {
    filesCount,
    allowedMimeTypes,
    addLibraryFile,
    handleLocalFile,
    handleExternalFile,
  } = useChatFileManager();

  const [isLibraryDialogOpen, setIsLibraryDialogOpen] = useState(false);
  const [isExternalFileDialogOpen, setIsExternalFileDialogOpen] =
    useState(false);

  const groupUuid = useGroupUuid();
  const kitUuid = useKitUuid();

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            size="icon"
            variant="ghost"
            type="button"
            disabled={props.disabled || filesCount >= MAX_FILES_COUNT}
            title="Add Files"
            aria-label="Add Files"
          >
            <FontAwesomeIcon icon={faPaperclip} />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start">
          <Tooltip>
            <TooltipContent side="left">
              {LIBRARY_FILE_ALLOWED_TEXT}
            </TooltipContent>
            <TooltipTrigger asChild>
              <DropdownMenuItem
                onClick={() => handleLocalFile()}
                className="flex flex-row items-center gap-2"
              >
                <FontAwesomeIcon icon={faUpload} /> Upload
              </DropdownMenuItem>
            </TooltipTrigger>
          </Tooltip>
          <Tooltip>
            <TooltipContent side="left">
              {LIBRARY_FILE_ALLOWED_TEXT}
            </TooltipContent>
            <TooltipTrigger asChild>
              <DropdownMenuItem
                onClick={() => setIsExternalFileDialogOpen(true)}
                className="flex flex-row items-center gap-2"
              >
                <FontAwesomeIcon icon={faCloudUpload} /> Connector
              </DropdownMenuItem>
            </TooltipTrigger>
          </Tooltip>
          {props.library && (
            <DropdownMenuItem onClick={() => setIsLibraryDialogOpen(true)}>
              <FontAwesomeIcon icon={faPaperclip} /> Library
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      {groupUuid && kitUuid && (
        <LibraryModal
          open={isLibraryDialogOpen}
          onOpenChange={(open) => {
            setIsLibraryDialogOpen(open);
          }}
          groupUuid={groupUuid}
          kitUuid={kitUuid}
          onSelect={async (file) => {
            await addLibraryFile(file);
          }}
        />
      )}

      {groupUuid && (
        <ExternalFileBrowserDialog
          groupUuid={groupUuid}
          allowedMimeTypes={allowedMimeTypes}
          open={isExternalFileDialogOpen}
          onOpenChange={(open) => {
            setIsExternalFileDialogOpen(open);
          }}
          mode="file"
          limit={1}
          onSelected={async (files) => {
            if (files.length !== 1 || files[0].kind !== "file") {
              toast.error("Invalid external file");
              return;
            }

            handleExternalFile(files[0]);
          }}
        />
      )}
    </>
  );
};
